import { create } from 'zustand'

/**
 * Embedded mode — Glass Office running inside the Zunou dashboard's Workforce
 * tab as an iframe (dashboard adds `?embedded=1&env=…` to the URL). The parent
 * posts `{ type: 'zunou-office:context', token, organizationId }` and we run as
 * that user in that org: no Auth0 login, no org picker, no sign-out — identity
 * and workspace are the DASHBOARD's job while framed. Standalone visits to
 * world.zunou.ai are completely unaffected (no `embedded` param → inert).
 *
 * The token never travels in the URL — only via postMessage from an
 * allowlisted parent origin, held in memory (never persisted, so it cannot
 * leak into a later standalone session's localStorage).
 */

const PARENT_ORIGINS = [
  'https://dashboard.zunou.ai',
  'https://dashboard.staging.zunou.ai',
]

// Local dashboard dev servers embed the deployed world too.
const isDevParent = (origin: string): boolean =>
  /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin)

/** True when this page was loaded as the dashboard's embedded office tab. */
export const embedded = ((): boolean => {
  try {
    return (
      new URLSearchParams(window.location.search).get('embedded') === '1' &&
      window.parent !== window
    )
  } catch {
    return false
  }
})()

interface EmbedState {
  token: string | null
  organizationId: string | null
}

export const useEmbedStore = create<EmbedState>(() => ({
  organizationId: null,
  token: null,
}))

/** Fresh read for non-React callers (token getter, Pusher auth). */
export const getEmbedToken = (): string | null => useEmbedStore.getState().token

/**
 * Attach the parent-message listener and tell the parent we are ready for
 * context. Called once from main.tsx before render; a no-op when standalone.
 */
export function initEmbed(): void {
  if (!embedded) return
  window.addEventListener('message', (event: MessageEvent) => {
    if (!PARENT_ORIGINS.includes(event.origin) && !isDevParent(event.origin)) {
      return
    }
    const data = event.data as {
      type?: string
      token?: string
      organizationId?: string
    } | null
    if (!data || data.type !== 'zunou-office:context') return
    useEmbedStore.setState({
      organizationId:
        typeof data.organizationId === 'string' && data.organizationId
          ? data.organizationId
          : null,
      token: typeof data.token === 'string' && data.token ? data.token : null,
    })
  })
  // The ready ping carries no data, so '*' is safe — the parent validates
  // OUR origin before answering with the context message.
  window.parent.postMessage({ type: 'zunou-office:ready' }, '*')
}
